import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Spinner, Alert, Badge, Form, Row, Col, Button, Pagination, Modal,
} from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import EMAIL_TEMPLATES from './emailTemplates';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 20;

const PLAN_OPTIONS = ['', 'Free', 'Basic', 'BasicPlus', 'Pro', 'Enterprise'];
const STATUS_OPTIONS = ['', 'active', 'trialing', 'canceled', 'grace', 'inactive', 'past_due'];

const STATUS_LABELS = {
  active:   'Activa',
  trialing: 'Prueba',
  canceled: 'Cancelada',
  grace:    'Gracia',
  inactive: 'Inactiva',
  past_due: 'Vencida',
};

const statusVariant = (status) => {
  if (!status) return 'secondary';
  switch (status.toLowerCase()) {
    case 'active':   return 'success';
    case 'trialing': return 'info';
    case 'grace':    return 'warning';
    case 'past_due': return 'danger';
    case 'canceled': return 'dark';
    case 'inactive': return 'secondary';
    default:         return 'secondary';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const buildQueryParams = (q) => {
  const params = new URLSearchParams();
  if (q.email)       params.set('email', q.email);
  if (q.plan)        params.set('plan', q.plan);
  if (q.status)      params.set('status', q.status);
  if (q.periodStart) params.set('periodStart', q.periodStart);
  if (q.periodEnd)   params.set('periodEnd', q.periodEnd);
  return params;
};

const AdminSendEmails = () => {
  const { getAuthHeaders } = useAuthHeaders();

  // ------- Filters -------
  const [emailInput, setEmailInput]             = useState('');
  const [planInput, setPlanInput]               = useState('');
  const [statusInput, setStatusInput]           = useState('');
  const [periodStartInput, setPeriodStartInput] = useState('');
  const [periodEndInput, setPeriodEndInput]     = useState('');

  const [query, setQuery] = useState({
    email: '', plan: '', status: '', periodStart: '', periodEnd: '',
    sortBy: 'email', sortDir: 'asc', page: 1, pageSize: PAGE_SIZE,
  });

  // ------- Users table -------
  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ------- Selection -------
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectionError, setSelectionError] = useState(null);

  // ------- Compose -------
  const [bodyMode, setBodyMode] = useState('html'); // 'html' | 'text'
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [fromName, setFromName] = useState('SatGo');
  const [showPreview, setShowPreview] = useState(false);
  const [templateId, setTemplateId] = useState('blank');

  const applyTemplate = (id) => {
    const tpl = EMAIL_TEMPLATES.find(t => t.id === id);
    if (!tpl) return;
    // Si ya hay contenido, confirmar antes de sobreescribir.
    if (html.trim() && !window.confirm('Esto reemplazará el HTML actual. ¿Continuar?')) {
      return;
    }
    setTemplateId(id);
    setHtml(tpl.build());
    setBodyMode('html');
  };

  // ------- Send modal / results -------
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { totalRequested, totalSent, totalFailed, items: [...] }
  const [sendError, setSendError] = useState(null);

  // ------- Fetch users -------
  const fetchUsers = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = buildQueryParams(q);
      params.set('sortBy',   q.sortBy);
      params.set('sortDir',  q.sortDir);
      params.set('page',     q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/users?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchUsers(query); }, [query, fetchUsers]);

  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      email: emailInput, plan: planInput, status: statusInput,
      periodStart: periodStartInput, periodEnd: periodEndInput,
      page: 1,
    }));
  };

  const handleClear = () => {
    setEmailInput(''); setPlanInput(''); setStatusInput('');
    setPeriodStartInput(''); setPeriodEndInput('');
    setQuery(prev => ({
      ...prev,
      email: '', plan: '', status: '', periodStart: '', periodEnd: '',
      page: 1,
    }));
  };

  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  // ------- Selection helpers -------
  const toggleOne = (email) => {
    if (!email) return;
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const pageEmails = (result.items ?? [])
    .map(u => u.email)
    .filter(Boolean);

  const pageAllSelected = pageEmails.length > 0 && pageEmails.every(e => selectedEmails.has(e));
  const pageSomeSelected = pageEmails.some(e => selectedEmails.has(e));

  const togglePage = () => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (pageAllSelected) {
        pageEmails.forEach(e => next.delete(e));
      } else {
        pageEmails.forEach(e => next.add(e));
      }
      return next;
    });
  };

  const handleSelectAllFiltered = async () => {
    setSelectingAll(true);
    setSelectionError(null);
    try {
      const headers = await getAuthHeaders();
      const params = buildQueryParams(query);
      const response = await fetch(`${API_BASE}/admin/users/emails?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const emails = await response.json();
      setSelectedEmails(prev => {
        const next = new Set(prev);
        emails.forEach(e => { if (e) next.add(e); });
        return next;
      });
    } catch (err) {
      setSelectionError(err.message);
    } finally {
      setSelectingAll(false);
    }
  };

  const handleClearSelection = () => setSelectedEmails(new Set());
  const removeOne = (email) => setSelectedEmails(prev => {
    const next = new Set(prev);
    next.delete(email);
    return next;
  });

  // ------- Send -------
  const canSend = selectedEmails.size > 0
    && subject.trim().length > 0
    && (bodyMode === 'html' ? html.trim().length > 0 : text.trim().length > 0);

  const openConfirm = () => {
    setSendResult(null);
    setSendError(null);
    setShowConfirm(true);
  };

  const handleSend = async () => {
    setSending(true);
    setSendError(null);
    setSendResult(null);
    try {
      const headers = await getAuthHeaders();
      const body = {
        recipients: Array.from(selectedEmails),
        subject: subject.trim(),
        html: bodyMode === 'html' ? html : null,
        text: bodyMode === 'text' ? text : null,
        fromName: fromName.trim() || 'SatGo',
      };
      const response = await fetch(`${API_BASE}/admin/send-emails`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        setSendError(data?.message || `Error ${response.status}`);
      } else {
        setSendResult(data);
      }
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const closeConfirm = () => {
    if (sending) return;
    setShowConfirm(false);
    // If we just finished a successful send, reset compose state.
    if (sendResult) {
      setSubject('');
      setHtml('');
      setText('');
      setTemplateId('blank');
      setSendResult(null);
      setSendError(null);
    }
  };

  // ------- Pagination items -------
  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;
  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) { items.push(1); if (left > 2) items.push('...'); }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) { if (right < totalPages - 1) items.push('...'); items.push(totalPages); }
    return items;
  };

  // ------- Render -------
  const selectedCount = selectedEmails.size;
  const selectedArray = Array.from(selectedEmails);

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Admin — Enviar Correos</h2>
          <p className="page-subtitle text-muted">
            Selecciona destinatarios por correo, plan o todos los filtrados, y envíales un correo individual vía Mailgun.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={3}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Buscar email..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Plan</Form.Label>
            <Form.Select size="sm" value={planInput} onChange={e => setPlanInput(e.target.value)}>
              {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p || 'Todos'}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Estado</Form.Label>
            <Form.Select size="sm" value={statusInput} onChange={e => setStatusInput(e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s ? (STATUS_LABELS[s] ?? s) : 'Todos'}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Inicio periodo</Form.Label>
            <Form.Control size="sm" type="date"
              value={periodStartInput}
              onChange={e => setPeriodStartInput(e.target.value)} />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Fin periodo</Form.Label>
            <Form.Control size="sm" type="date"
              value={periodEndInput}
              onChange={e => setPeriodEndInput(e.target.value)} />
          </Col>
          <Col xs={12} md={1} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">Buscar</Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">✕</Button>
          </Col>
        </Row>

        {/* Bulk selection actions */}
        <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleSelectAllFiltered}
            disabled={selectingAll || loading}
          >
            {selectingAll
              ? <><Spinner animation="border" size="sm" /> Seleccionando…</>
              : `Seleccionar todos los filtrados (${result.totalCount ?? 0})`}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearSelection}
            disabled={selectedCount === 0}
          >
            Limpiar selección
          </Button>
          <span className="text-muted small ms-auto">
            {selectedCount} destinatario{selectedCount !== 1 ? 's' : ''} seleccionado{selectedCount !== 1 ? 's' : ''}
          </span>
        </div>
        {selectionError && <Alert variant="danger" className="mt-2 mb-0 py-2 small">Error: {selectionError}</Alert>}
      </div>

      {error && <Alert variant="danger">Error al cargar usuarios: {error}</Alert>}

      {/* Users table */}
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 32 }}>
                    <Form.Check
                      type="checkbox"
                      checked={pageAllSelected}
                      ref={el => { if (el) el.indeterminate = !pageAllSelected && pageSomeSelected; }}
                      onChange={togglePage}
                      title="Seleccionar página"
                    />
                  </th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Inicio periodo</th>
                  <th>Fin periodo</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((u) => (
                  <tr key={u.userUuid}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedEmails.has(u.email)}
                        onChange={() => toggleOne(u.email)}
                        disabled={!u.email}
                      />
                    </td>
                    <td>{u.email}</td>
                    <td>{u.planCode ?? '—'}</td>
                    <td>
                      <Badge bg={statusVariant(u.subscriptionStatus)}>
                        {u.subscriptionStatus ? (STATUS_LABELS[u.subscriptionStatus] ?? u.subscriptionStatus) : 'Sin suscripción'}
                      </Badge>
                    </td>
                    <td>{formatDate(u.currentPeriodStartUtc)}</td>
                    <td>{formatDate(u.currentPeriodEndUtc)}</td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">No se encontraron usuarios con los filtros aplicados.</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages} ({result.totalCount} resultados)
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Selected chips */}
      {selectedCount > 0 && (
        <div className="mt-4 p-3 border rounded">
          <div className="d-flex align-items-center mb-2">
            <strong className="me-2">Destinatarios ({selectedCount})</strong>
          </div>
          <div className="d-flex flex-wrap gap-1" style={{ maxHeight: 160, overflowY: 'auto' }}>
            {selectedArray.map(email => (
              <Badge
                key={email}
                bg="light"
                text="dark"
                className="border"
                style={{ cursor: 'pointer', padding: '6px 10px' }}
                onClick={() => removeOne(email)}
                title="Quitar"
              >
                {email} <span className="text-muted ms-1">✕</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Compose form */}
      <div className="mt-4 p-3 border rounded">
        <h5 className="mb-3">Redactar correo</h5>
        <Row className="g-2 mb-2">
          <Col xs={12} md={4}>
            <Form.Label className="small mb-1">Remitente (nombre)</Form.Label>
            <Form.Control
              size="sm"
              value={fromName}
              onChange={e => setFromName(e.target.value)}
              placeholder="SatGo"
            />
            <Form.Text className="text-muted">Se envía desde <code>contacto@sat-go.com</code>.</Form.Text>
          </Col>
          <Col xs={12} md={8}>
            <Form.Label className="small mb-1">Asunto</Form.Label>
            <Form.Control
              size="sm"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Asunto del correo"
            />
          </Col>
        </Row>

        <Row className="g-2 align-items-end mb-2">
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">Plantilla predefinida</Form.Label>
            <Form.Select
              size="sm"
              value={templateId}
              onChange={e => applyTemplate(e.target.value)}
            >
              {EMAIL_TEMPLATES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Carga un HTML con estilo SatGo. Reemplaza los textos entre corchetes [ ] con tu contenido.
            </Form.Text>
          </Col>
        </Row>

        <Form.Label className="small mb-1">Cuerpo</Form.Label>
        <div className="mb-2 d-flex gap-2 align-items-center">
          <Form.Check
            type="radio"
            id="body-mode-html"
            name="bodyMode"
            label="HTML"
            checked={bodyMode === 'html'}
            onChange={() => setBodyMode('html')}
          />
          <Form.Check
            type="radio"
            id="body-mode-text"
            name="bodyMode"
            label="Texto plano"
            checked={bodyMode === 'text'}
            onChange={() => setBodyMode('text')}
          />
          {bodyMode === 'html' && (
            <Button
              size="sm"
              variant="link"
              className="ms-auto p-0"
              onClick={() => setShowPreview(p => !p)}
              disabled={!html.trim()}
            >
              {showPreview ? 'Ocultar vista previa' : 'Vista previa'}
            </Button>
          )}
        </div>

        {bodyMode === 'html' ? (
          <Form.Control
            as="textarea"
            rows={12}
            value={html}
            onChange={e => setHtml(e.target.value)}
            placeholder="<h1>Hola</h1><p>Mensaje...</p>"
            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
          />
        ) : (
          <Form.Control
            as="textarea"
            rows={12}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Escribe tu mensaje aquí..."
          />
        )}

        {bodyMode === 'html' && showPreview && html.trim() && (
          <div className="mt-3 p-3 border rounded bg-white" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <div className="text-muted small mb-2">Vista previa:</div>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        )}

        <div className="mt-3 d-flex justify-content-end">
          <Button variant="primary" onClick={openConfirm} disabled={!canSend}>
            Enviar a {selectedCount} destinatario{selectedCount !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>

      {/* Confirm + results modal */}
      <Modal show={showConfirm} onHide={closeConfirm} centered size="lg" backdrop={sending ? 'static' : true}>
        <Modal.Header closeButton={!sending}>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {sendResult ? 'Resultado del envío' : 'Confirmar envío'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!sendResult && !sending && (
            <>
              <p>Se enviarán <strong>{selectedCount}</strong> correos individuales (uno por destinatario).</p>
              <p className="small text-muted mb-0">
                <strong>Asunto:</strong> {subject}<br />
                <strong>De:</strong> {fromName || 'SatGo'} &lt;contacto@sat-go.com&gt;<br />
                <strong>Formato:</strong> {bodyMode === 'html' ? 'HTML' : 'Texto plano'}
              </p>
            </>
          )}

          {sending && (
            <div className="d-flex align-items-center justify-content-center py-4 flex-column gap-2">
              <Spinner animation="border" style={{ color: '#13a0ff' }} />
              <small className="text-muted">Enviando {selectedCount} correos...</small>
            </div>
          )}

          {sendError && (
            <Alert variant="danger" className="mb-0">
              No se pudo enviar: {sendError}
            </Alert>
          )}

          {sendResult && (
            <>
              <Alert variant={sendResult.totalFailed === 0 ? 'success' : 'warning'}>
                Enviados: <strong>{sendResult.totalSent}</strong> · Fallidos: <strong>{sendResult.totalFailed}</strong> · Total: {sendResult.totalRequested}
              </Alert>
              {sendResult.totalFailed > 0 && (
                <>
                  <div className="small text-muted mb-2">Correos con error:</div>
                  <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <Table size="sm" bordered>
                      <thead className="table-light">
                        <tr><th>Email</th><th>Error</th></tr>
                      </thead>
                      <tbody>
                        {sendResult.items.filter(i => !i.success).map(i => (
                          <tr key={i.email}>
                            <td>{i.email}</td>
                            <td className="small text-danger">{i.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!sendResult && !sending && (
            <>
              <Button variant="secondary" size="sm" onClick={closeConfirm}>Cancelar</Button>
              <Button variant="primary" size="sm" onClick={handleSend}>Confirmar envío</Button>
            </>
          )}
          {(sendResult || sendError) && !sending && (
            <Button variant="secondary" size="sm" onClick={closeConfirm}>Cerrar</Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminSendEmails;
